import React, { useEffect, useState } from "react";
import {
  AdminPanelSettingsOutlined,
  ChevronRightRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  GridViewRounded,
  Inventory2Outlined,
  MonetizationOnOutlined,
  PeopleAltOutlined,
  TodayOutlined,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import profileImage from "assets/profile.jpeg";
import logoImage from "assets/logo-prima-trans.png";

const navItems = [
  {
    text: "Dashboard",
    path: "dashboard",
    permission: "dashboard",
    icon: <GridViewRounded />,
    section: "Workspace",
  },
  {
    text: "Data Master",
    icon: <PeopleAltOutlined />,
    section: "Workspace",
    children: [
      { text: "Departemen", path: "departemen", permission: "departemen" },
      { text: "Divisi", path: "divisi", permission: "divisi" },
      { text: "Jabatan", path: "jabatan", permission: "jabatan" },
      { text: "Karyawan", path: "karyawanbaru", permission: "karyawan" },
      { text: "Notifikasi", path: "notifikasi", permission: "notifikasi" },
      { text: "Panduan", path: "panduan", permission: "panduan" },
      { text: "Aspirasi", path: "aspirasi", permission: "aspirasi" },
      { text: "Peringatan", path: "peringatan", permission: "peringatan" },
      { text: "Jenjang", path: "jenjang", permission: "jenjang" },
    ],
  },
  {
    text: "Stock opname",
    icon: <Inventory2Outlined />,
    section: "Operations",
    children: [
      {
        text: "Data stock opname",
        path: "stockopname",
        permission: "stockopname",
      },
      {
        text: "Peserta stock opname",
        path: "pesertaso",
        permission: "pesertaso",
      },
    ],
  },
  {
    text: "Absensi",
    icon: <TodayOutlined />,
    section: "Operations",
    children: [
      {
        text: "Absensi hari ini",
        path: "absensihariini",
        permission: "absensihariini",
      },
      {
        text: "Rekap absen harian",
        path: "rekapabsensiharian",
        permission: "rekapabsensiharian",
      },
      {
        text: "Rekap absen bulanan",
        path: "rekapabsensibulanan",
        permission: "rekapabsensibulanan",
      },
    ],
  },
  {
    text: "Keuangan",
    icon: <MonetizationOnOutlined />,
    section: "Finance",
    children: [
      { text: "Tabungan", path: "tabungan", permission: "tabungan" },
      {
        text: "Rekap tabungan",
        path: "rekaptabungan",
        permission: "rekaptabungan",
      },
      { text: "Gaji karyawan", path: "penggajian", permission: "penggajian" },
      { text: "Setoran toko", path: "setoran", permission: "setoran" },
      { text: "Alokasi DC", path: "alokasidc", permission: "alokasidc" },
      { text: "Buku warkat", path: "bukuwarkat", permission: "bukuwarkat" },
    ],
  },
  {
    text: "Role management",
    path: "rolemanagement",
    permission: "role_management",
    icon: <AdminPanelSettingsOutlined />,
    section: "System",
  },
];

const Sidebar = ({
  drawerWidth = 268,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { websiteUser, user_data } = useSelector((state) => state.user);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const permissionKey = websiteUser?.permissions?.join("|") || "";

  const hasAccess = (permission) =>
    !permission || Boolean(websiteUser?.permissions?.includes(permission));

  const visibleNavItems = navItems
    .map((item) => ({
      ...item,
      children: item.children?.filter((child) => hasAccess(child.permission)),
    }))
    .filter((item) =>
      item.children ? item.children.length > 0 : hasAccess(item.permission),
    );

  useEffect(() => {
    setOpenSubmenus((previous) => {
      const next = { ...previous };
      navItems.forEach((item) => {
        if (item.children?.some((child) => pathname === `/${child.path}`))
          next[item.text] = true;
      });
      return next;
    });
  }, [pathname, permissionKey]);

  const goTo = (path) => {
    navigate(`/${path}`);
    if (!isNonMobile) setIsSidebarOpen(false);
  };

  const displayName = user_data?.nama_karyawan || "Workspace user";
  const initials = displayName
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <Box
      component="nav"
      sx={{ width: isNonMobile ? drawerWidth : 0, flexShrink: 0 }}
    >
      <Drawer
        variant={isNonMobile ? "persistent" : "temporary"}
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            border: 0,
            color: "#ffe1e1",
            background: theme.palette.primary[900],
            overflowX: "hidden",
          },
        }}
      >
        <Box sx={{ px: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="Prima Centre"
              sx={{
                width: 150,
                height: "auto",
                display: "block",
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            px: 1.5,
            flex: 1,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {visibleNavItems.map((item, index) => {
            const previous = visibleNavItems[index - 1];
            const isGroupStart = !previous || previous.section !== item.section;
            const itemActive = pathname === `/${item.path}`;
            const childActive = item.children?.some(
              (child) => pathname === `/${child.path}`,
            );
            const isOpen = openSubmenus[item.text] || childActive;

            return (
              <Box key={item.text}>
                {isGroupStart && (
                  <Typography
                    sx={{
                      px: 1.5,
                      pt: index === 0 ? 1 : 2.75,
                      pb: 1,
                      color: "#ff9d9e",
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                    }}
                  >
                    {item.section}
                  </Typography>
                )}
                <ListItem disablePadding sx={{ mb: 0.45 }}>
                  <ListItemButton
                    onClick={() =>
                      item.children
                        ? setOpenSubmenus((prev) => ({
                            ...prev,
                            [item.text]: !isOpen,
                          }))
                        : goTo(item.path)
                    }
                    sx={{
                      minHeight: 44,
                      px: 1.5,
                      borderRadius: 2.5,
                      color: itemActive || childActive ? "#fff" : "#ffc3c4",
                      bgcolor: itemActive
                        ? alpha(theme.palette.secondary.main, 0.18)
                        : "transparent",
                      "&:hover": {
                        bgcolor: alpha("#fff", 0.07),
                        color: "#fff",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color:
                          itemActive || childActive
                            ? theme.palette.secondary[300]
                            : "#ff9d9e",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: 12,
                        fontWeight: itemActive || childActive ? 700 : 600,
                      }}
                    />
                    {item.children ? (
                      isOpen ? (
                        <ExpandLessRounded sx={{ fontSize: 19 }} />
                      ) : (
                        <ExpandMoreRounded sx={{ fontSize: 19 }} />
                      )
                    ) : itemActive ? (
                      <ChevronRightRounded sx={{ fontSize: 19 }} />
                    ) : null}
                  </ListItemButton>
                </ListItem>
                {item.children && (
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List disablePadding sx={{ pl: 2.2, pb: 0.5 }}>
                      {item.children.map((child) => {
                        const active = pathname === `/${child.path}`;
                        return (
                          <ListItem
                            key={child.path}
                            disablePadding
                            sx={{ mb: 0.25 }}
                          >
                            <ListItemButton
                              onClick={() => goTo(child.path)}
                              sx={{
                                minHeight: 34,
                                px: 1.5,
                                borderRadius: 2,
                                color: active ? "#fff" : "#ffc3c4",
                                bgcolor: active
                                  ? alpha(theme.palette.secondary.main, 0.16)
                                  : "transparent",
                                "&:hover": {
                                  color: "#fff",
                                  bgcolor: alpha("#fff", 0.06),
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 5,
                                  height: 5,
                                  mr: 1.5,
                                  borderRadius: "50%",
                                  bgcolor: active
                                    ? theme.palette.secondary[300]
                                    : "#a82932",
                                }}
                              />
                              <ListItemText
                                primary={child.text}
                                primaryTypographyProps={{
                                  fontSize: 11.5,
                                  fontWeight: active ? 700 : 500,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            m: 1.5,
            p: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1.1,
            border: `1px solid ${alpha("#fff", 0.09)}`,
            borderRadius: 3,
            bgcolor: alpha("#fff", 0.04),
          }}
        >
          <Box
            component="img"
            src={profileImage}
            alt="Profile"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ color: "#fff", fontWeight: 700, fontSize: 11.5 }}
            >
              {displayName}
            </Typography>
            <Typography noWrap sx={{ color: "#ff9d9e", fontSize: 10 }}>
              {initials || "PC"} · Admin workspace
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Sidebar;
